import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  User, 
  Lock, 
  Bell, 
  Shield,
  Settings,
  Eye,
  EyeOff,
  Save,
  CheckCircle,
  AlertTriangle,
  Smartphone,
  Mail,
  Key,
  Clock,
  Globe,
  Palette
} from 'lucide-react';

const CustodianSettings = () => {
  const { changePassword, isChangingPassword, updateProfile, user } = useAuth();
  const [activeTab, setActiveTab] = useState(user?.mustChangePassword ? 'security' : 'profile');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedTab, setSavedTab] = useState('');

  // Profile state - initialize with user data or defaults
  const [profile, setProfile] = useState({
    firstName: user?.personalInfo?.firstName || '',
    lastName: user?.personalInfo?.lastName || '',
    email: user?.email || '',
    phone: user?.personalInfo?.phoneNumber || '',
    timezone: user?.preferences?.timezone || 'Asia/Kuala_Lumpur',
    language: user?.preferences?.language || 'en'
  });

  // Update profile state when user data changes
  useEffect(() => {
    if (user) {
      setProfile({
        firstName: user?.personalInfo?.firstName || '',
        lastName: user?.personalInfo?.lastName || '',
        email: user?.email || '',
        phone: user?.personalInfo?.phoneNumber || '',
        timezone: user?.preferences?.timezone || 'Asia/Kuala_Lumpur',
        language: user?.preferences?.language || 'en'
      });
    }
  }, [user]);

  // Auto-switch to security tab if password change is required
  useEffect(() => {
    if (user?.mustChangePassword && activeTab !== 'security') {
      setActiveTab('security');
    }
  }, [user?.mustChangePassword]);

  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    email: {
      kycApplications: true,
      paymentAlerts: true,
      distributionReminders: true,
      systemMaintenance: false,
      securityAlerts: true,
      complianceIssues: true
    },
    sms: {
      urgentAlerts: true,
      securityAlerts: true,
      systemDown: false
    },
    inApp: {
      taskReminders: true,
      newMessages: true,
      statusUpdates: true
    }
  });

  // Security settings
  const [security, setSecurity] = useState({
    twoFactorEnabled: true,
    twoFactorMethod: 'authenticator',
    sessionTimeout: 30,
    loginNotifications: true,
    ipWhitelist: true,
    deviceTracking: true
  });

  // System preferences
  const [systemPrefs, setSystemPrefs] = useState({
    theme: 'light',
    compactMode: false,
    showTooltips: true,
    defaultPageSize: 20,
    dateFormat: 'DD/MM/YYYY',
    numberFormat: 'en-MY',
    currency: 'MYR'
  });

  const tabs = [
    {
      id: 'profile',
      name: 'Profile',
      icon: User,
      description: 'Personal information and contact details'
    },
    {
      id: 'security',
      name: 'Security',
      icon: Shield,
      description: 'Password, 2FA, and security settings'
    }
    // Temporarily hidden tabs:
    // {
    //   id: 'notifications',
    //   name: 'Notifications',
    //   icon: Bell,
    //   description: 'Email, SMS, and in-app notification preferences'
    // },
    // {
    //   id: 'preferences',
    //   name: 'Preferences',
    //   icon: Settings,
    //   description: 'System display and behavior settings'
    // }
  ];

  const handleSave = async (tabId) => {
    if (tabId === 'security') {
      // Handle password change
      const validationError = validatePassword();
      if (validationError) {
        toast.error(validationError);
        return;
      }

      // For first-time password change, current password is optional
      const isFirstTimeChange = user?.mustChangePassword === true;
      
      if (!passwordData.newPassword) {
        toast.error('Please enter a new password');
        return;
      }

      if (!isFirstTimeChange && !passwordData.currentPassword) {
        toast.error('Please enter your current password');
        return;
      }

      try {
        setSaving(true);
        await changePassword({
          currentPassword: isFirstTimeChange ? undefined : passwordData.currentPassword,
          newPassword: passwordData.newPassword
        });
        
        // Clear password fields after successful change
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        setSavedTab(tabId);
        setTimeout(() => {
          setSavedTab('');
        }, 3000);
      } catch (error) {
        // Error handling is done in the mutation
      } finally {
        setSaving(false);
      }
    } else if (tabId === 'profile') {
      // Handle profile update
      try {
        setSaving(true);
        
        // Prepare profile data for API
        const profileData = {
          personalInfo: {
            firstName: profile.firstName,
            lastName: profile.lastName,
            phoneNumber: profile.phone
          },
          preferences: {
            timezone: profile.timezone,
            language: profile.language
          }
        };

        await updateProfile(profileData);
        
        setSavedTab(tabId);
        setTimeout(() => {
          setSavedTab('');
        }, 3000);
      } catch (error) {
        // Error handling is done in the mutation
      } finally {
        setSaving(false);
      }
    } else {
      // Handle other settings (simulate API call for now)
      setSaving(true);
      setTimeout(() => {
        setSaving(false);
        setSavedTab(tabId);
        
        // Clear saved indicator after 3 seconds
        setTimeout(() => {
          setSavedTab('');
        }, 3000);
      }, 1000);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleNotificationChange = (category, setting, value) => {
    setNotifications(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value
      }
    }));
  };

  const handleSecurityChange = (field, value) => {
    setSecurity(prev => ({ ...prev, [field]: value }));
  };

  const handlePreferenceChange = (field, value) => {
    setSystemPrefs(prev => ({ ...prev, [field]: value }));
  };

  const validatePassword = () => {
    const { newPassword, confirmPassword } = passwordData;
    if (newPassword !== confirmPassword) {
      return 'Passwords do not match';
    }
    if (newPassword.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return null;
  };

  // Only show password validation errors after user starts typing
  const passwordValidationError = validatePassword();
  const shouldShowPasswordError =
    (passwordData.newPassword.length > 0 || passwordData.confirmPassword.length > 0) &&
    !!passwordValidationError;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
              <p className="text-sm text-gray-500 mt-1">Manage your personal information, security settings, and system preferences</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Navigation Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-500'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-3" />
                    <span>{tab.name}</span>
                    {savedTab === tab.id && (
                      <CheckCircle className="h-4 w-4 ml-auto text-green-500" />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="bg-white border border-light-gray rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Profile Information</h3>
                  <button
                    onClick={() => handleSave('profile')}
                    disabled={saving}
                    className="btn-primary"
                  >
                    {saving ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={profile.firstName}
                      onChange={(e) => handleProfileChange('firstName', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={profile.lastName}
                      onChange={(e) => handleProfileChange('lastName', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={profile.email}
                      onChange={(e) => handleProfileChange('email', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => handleProfileChange('phone', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timezone
                    </label>
                    <select
                      value={profile.timezone}
                      onChange={(e) => handleProfileChange('timezone', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="Asia/Kuala_Lumpur">Asia/Kuala Lumpur (GMT+8)</option>
                      <option value="Asia/Singapore">Asia/Singapore (GMT+8)</option>
                      <option value="UTC">UTC (GMT+0)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Language
                    </label>
                    <select
                      value={profile.language}
                      onChange={(e) => handleProfileChange('language', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="en">English</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                {/* Change Password */}
                <div className="bg-white border border-light-gray rounded-lg p-6">
                  {user?.mustChangePassword && (
                    <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mr-2 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-amber-800 mb-1">
                            Password Change Required
                          </h4>
                          <p className="text-sm text-amber-700">
                            You must change your password before you can access other pages. Please set a new password to continue.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
                    <button
                      onClick={() => handleSave('security')}
                      disabled={
                        saving || 
                        validatePassword() || 
                        !passwordData.newPassword ||
                        (!user?.mustChangePassword && !passwordData.currentPassword)
                      }
                      className="btn-primary"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {user?.mustChangePassword ? 'Set Password' : 'Update Password'}
                    </button>
                  </div>

                  <div className="space-y-4 max-w-md">
                    {!user?.mustChangePassword && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Current Password
                        </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={passwordData.currentPassword}
                          onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                          className="w-full pr-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showCurrentPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={passwordData.newPassword}
                          onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                          className="w-full pr-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showNewPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={passwordData.confirmPassword}
                          onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                          className={`w-full pr-10 rounded-md shadow-sm focus:ring-blue-500 ${
                            shouldShowPasswordError
                              ? 'border-red-300 focus:border-red-500'
                              : 'border-gray-300 focus:border-blue-500'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                      {shouldShowPasswordError && (
                        <p className="mt-1 text-sm text-red-600">{passwordValidationError}</p>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Notification Preferences</h3>
                  <button
                    onClick={() => handleSave('notifications')}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Preferences
                  </button>
                </div>

                <div className="space-y-8">
                  {/* Email Notifications */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      <Mail className="h-5 w-5 mr-2" />
                      Email Notifications
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(notifications.email).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) => handleNotificationChange('email', key, e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* SMS Notifications */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      <Smartphone className="h-5 w-5 mr-2" />
                      SMS Notifications
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(notifications.sms).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) => handleNotificationChange('sms', key, e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* In-App Notifications */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      <Bell className="h-5 w-5 mr-2" />
                      In-App Notifications
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(notifications.inApp).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) => handleNotificationChange('inApp', key, e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">System Preferences</h3>
                  <button
                    onClick={() => handleSave('preferences')}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Preferences
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Theme
                    </label>
                    <select
                      value={systemPrefs.theme}
                      onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="auto">Auto (System)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date Format
                    </label>
                    <select
                      value={systemPrefs.dateFormat}
                      onChange={(e) => handlePreferenceChange('dateFormat', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number Format
                    </label>
                    <select
                      value={systemPrefs.numberFormat}
                      onChange={(e) => handlePreferenceChange('numberFormat', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="en-MY">Malaysia (1,234.56)</option>
                      <option value="en-US">US (1,234.56)</option>
                      <option value="de-DE">German (1.234,56)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Currency
                    </label>
                    <select
                      value={systemPrefs.currency}
                      onChange={(e) => handlePreferenceChange('currency', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="MYR">Malaysian Ringgit (MYR)</option>
                      <option value="USD">US Dollar (USD)</option>
                      <option value="SGD">Singapore Dollar (SGD)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Page Size
                    </label>
                    <select
                      value={systemPrefs.defaultPageSize}
                      onChange={(e) => handlePreferenceChange('defaultPageSize', parseInt(e.target.value))}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value={10}>10 items</option>
                      <option value={20}>20 items</option>
                      <option value={50}>50 items</option>
                      <option value={100}>100 items</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Compact Mode</p>
                      <p className="text-sm text-gray-500">Show more content in less space</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={systemPrefs.compactMode}
                        onChange={(e) => handlePreferenceChange('compactMode', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Show Tooltips</p>
                      <p className="text-sm text-gray-500">Display helpful tooltips on hover</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={systemPrefs.showTooltips}
                        onChange={(e) => handlePreferenceChange('showTooltips', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustodianSettings; 